import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ThemeProvider } from "./components/theme-provider";
import { AppToaster } from "./components/AppToaster";
import { UIPresetProvider } from "./components/ui-preset-provider";
import { isAuthenticated } from "./utils/auth";

/* ─── Lazy-loaded pages (code splitting for faster initial load) ─── */
const DashboardPage = lazy(() => import("./pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const TenantsPage = lazy(() => import("./pages/TenantsPage").then(m => ({ default: m.TenantsPage })));
const LocationsPage = lazy(() => import("./pages/LocationsPage").then(m => ({ default: m.LocationsPage })));
const EquipmentPage = lazy(() => import("./pages/EquipmentPage").then(m => ({ default: m.EquipmentPage })));
const AlertsPage = lazy(() => import("./pages/AlertsPage").then(m => ({ default: m.AlertsPage })));
const AlertRulesPage = lazy(() => import("./pages/AlertRulesPage").then(m => ({ default: m.AlertRulesPage })));
const ReportsPage = lazy(() => import("./pages/ReportsPage").then(m => ({ default: m.ReportsPage })));
const MaintenancePage = lazy(() => import("./pages/MaintenancePage").then(m => ({ default: m.MaintenancePage })));
const AuditLogsPage = lazy(() => import("./pages/AuditLogsPage").then(m => ({ default: m.AuditLogsPage })));
const UsersPage = lazy(() => import("./pages/UsersPage").then(m => ({ default: m.UsersPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const EquipmentTypesPage = lazy(() => import("./pages/EquipmentTypesPage").then(m => ({ default: m.EquipmentTypesPage })));
const GatewaySetupPage = lazy(() => import("./pages/GatewaySetupPage").then(m => ({ default: m.GatewaySetupPage })));
const DataFlowPage = lazy(() => import("./pages/DataFlowPage").then(m => ({ default: m.DataFlowPage })));
const InventoryPage = lazy(() => import("./pages/InventoryPage").then(m => ({ default: m.InventoryPage })));
const LogBookPage = lazy(() => import("./pages/LogBookPage").then(m => ({ default: m.LogBookPage })));
const IncidentsPage = lazy(() => import("./pages/IncidentsPage").then(m => ({ default: m.IncidentsPage })));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const LiveReadingsPage = lazy(() => import("./pages/LiveReadingsPage").then(m => ({ default: m.LiveReadingsPage })));
const AcceptInvitePage = lazy(() => import("./pages/AcceptInvitePage").then(m => ({ default: m.AcceptInvitePage })));

/* ─── Minimal loading spinner ─── */
const PageLoader = () => (
  <div className="flex h-screen w-full items-center justify-center bg-white dark:bg-zinc-950">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700 dark:border-zinc-600 dark:border-t-zinc-300" />
  </div>
);

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <UIPresetProvider>
          <BrowserRouter>
            <AppToaster />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/login" element={isAuthenticated() ? <Navigate to="/dashboard" /> : <LoginPage />} />
                <Route path="/register" element={isAuthenticated() ? <Navigate to="/dashboard" /> : <RegisterPage />} />
                <Route path="/accept-invite" element={<AcceptInvitePage />} />
                
                <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
                <Route path="/tenants" element={<ProtectedRoute><TenantsPage /></ProtectedRoute>} />
                <Route path="/locations" element={<ProtectedRoute><LocationsPage /></ProtectedRoute>} />
                <Route path="/equipment" element={<ProtectedRoute><EquipmentPage /></ProtectedRoute>} />
                <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
                <Route path="/equipment-types" element={<ProtectedRoute><EquipmentTypesPage /></ProtectedRoute>} />
                <Route path="/gateway-setup" element={<ProtectedRoute><GatewaySetupPage /></ProtectedRoute>} />
                <Route path="/data-flow" element={<ProtectedRoute><DataFlowPage /></ProtectedRoute>} />
                <Route path="/alerts" element={<ProtectedRoute><AlertsPage /></ProtectedRoute>} />
                <Route path="/alert-rules" element={<ProtectedRoute><AlertRulesPage /></ProtectedRoute>} />
                <Route path="/live-readings" element={<ProtectedRoute><LiveReadingsPage /></ProtectedRoute>} />
                <Route path="/log-book" element={<ProtectedRoute><LogBookPage /></ProtectedRoute>} />
                <Route path="/incidents" element={<ProtectedRoute><IncidentsPage /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
                <Route path="/maintenance" element={<ProtectedRoute><MaintenancePage /></ProtectedRoute>} />
                <Route path="/audit-logs" element={<ProtectedRoute><AuditLogsPage /></ProtectedRoute>} />
                <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                
                <Route path="/" element={<Navigate to="/dashboard" />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </UIPresetProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
